import React, { Suspense } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { ThemeProvider } from '@material-ui/styles';
import Routes from './Routes';
import { connect } from 'react-redux';
import * as actions from './store/actions';
import theme from './theme';
import {
  CircularProgress,
  makeStyles,
  Backdrop,
  Snackbar
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { getCookie, setCookie, clearAllCookies } from 'helpers/saveToCookies';
import {
  USER_LOGIN_DATA,
  PARTNER_ID,
  DEVICE_INFORMATION,
  CODE_RAYA_APPS,
  ERROR_EXCEPTION_HANDLING,
  WEAK_PIN_REDIRECT_MAP,
  SCHEME_CODE_PNANG,
  CODE_AUTH_FLOW_RAYA_0001,
  CODE_AUTH_FLOW_RAYA_0007,
  PHONE_PREFIX_62TO08
} from 'common/constants';
import CryptoJS from 'crypto-js';
import {
  CustomDrawer,
  DrawerInfo,
  DrawerMaintenance,
  WarningDrawerContent
} from 'components';
import {
  getDecryptJwtToken,
  getDecryptRayaAppsData,
  getDeviceInformation
} from 'helpers/getQueryStringUrl';
import SafeAreaView from 'components/SafeAreaView';
import useGoBack from './hooks/useGoBack';
import closeWebview from './helpers/closeWebview';
import { pdfjs } from 'react-pdf';
import { useGeolocation } from './hooks/useGeolocation';
import { dueDateTimeFormat } from 'helpers/addDate';
import { phonePrefix } from 'helpers';
import PropTypes from 'prop-types';
import { formatDateIndonesia } from 'helpers/Utility';

pdfjs.GlobalWorkerOptions.workerSrc =
  'https://unpkg.com/pdfjs-dist@2.12.313/build/pdf.worker.min.js';

const useStyles = makeStyles(() => ({
  content: {
    backgroundColor: 'transparent',
    maxWidth: '500px',
    margin: '0 auto'
  },
  backdrop: {
    zIndex: 1305,
    color: '#fff'
  },
  snackbarAlert: {
    width: '470px'
  }
}));

const App = props => {
  const {
    appLoad,
    setApplicationStatus,
    setParameterAccess,
    setDeviceInformation,
    descriptionBottomSheet,
    tittleBottomSheet,
    isMaintenance,
    setIsMaintenance,
    setDataRayaApps,
    setPhoneNumber,
    setPhoneNumberRaya,
    setPhoneNumberFlexiNew,
    setEktp,
    setEktpRaya,
    setCustomerName,
    setStatusAuthFlowRaya,
    setCodeAuthFlowRaya,
    setIsRayaAgreement,
    setError,
    error,
    resetError,
    isSecondApply,
    nextEligibilityDate,
    schemeCode,
    subCode,
    codeAuthFlowRaya,
    phoneNoRaya,
    phoneRegistration,
    showSuccessChangeNumber,
    setShowSuccessChangeNumber,
    userProfile
  } = props;
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);

  const classes = useStyles();
  const history = useHistory();
  const [isExitPopup, setIsExitPopup] = useGoBack();
  const [showPostalDrawer, setShowPostalDrawer] = React.useState(false);

  /**
   * useGeolocation hook to get user's location
   */
  useGeolocation();

  React.useLayoutEffect(() => {
    const decodedToken = getDecryptJwtToken();
    const deviceInformation = getDeviceInformation();
    const decodedDataRayaApps = getDecryptRayaAppsData();

    /**
     * set decode token from url { partnerId, clientSecret, clientId, VTtoken, subCode } for set to cookie and redux
     */
    if (decodedToken) {
      const encryptDecodedToken = CryptoJS.AES.encrypt(
        JSON.stringify(decodedToken),
        process.env.REACT_APP_SECRET_KEY
      ).toString();

      setCookie(USER_LOGIN_DATA, encryptDecodedToken);
      setCookie(PARTNER_ID, decodedToken.partnerId, 10);
      setParameterAccess(decodedToken);
    }

    /**
     * clear all cookie and localStorage when USER_LOGIN_DATA cookie is missing
     */
    if (!getCookie(USER_LOGIN_DATA)) {
      if (!sessionStorage.getItem('_cleared')) {
        clearAllCookies();
        localStorage.clear();
        sessionStorage.setItem('_cleared', '1');
        window.location.reload();
      }
    } else {
      sessionStorage.removeItem('_cleared');
    }

    /**
     * set decode device information from url { deviceId, deviceType, deviceName } for set to cookie and redux
     */
    if (deviceInformation) {
      setCookie(DEVICE_INFORMATION, JSON.stringify(deviceInformation), 10);
      setDeviceInformation(deviceInformation);
    }

    /**
     * set decode data raya apps auth flow sso from url { noHpFlexi, noHpRaya, noHpFlexiNew, name, nikFlexi, nikRaya, isRayaAgreement, status } for set to cookie and redux
     */
    if (decodedDataRayaApps) {
      setDataRayaApps(decodedDataRayaApps);

      if (decodedDataRayaApps?.noHpFlexi)
        setPhoneNumber(decodedDataRayaApps?.noHpFlexi);
      if (decodedDataRayaApps?.noHpRaya)
        setPhoneNumberRaya(decodedDataRayaApps?.noHpRaya);
      if (decodedDataRayaApps?.noHpFlexiNew)
        setPhoneNumberFlexiNew(decodedDataRayaApps?.noHpFlexiNew);
      if (decodedDataRayaApps?.nikFlexi) setEktp(decodedDataRayaApps?.nikFlexi);
      if (decodedDataRayaApps?.nikRaya)
        setEktpRaya(decodedDataRayaApps?.nikRaya);
      if (decodedDataRayaApps?.name) setCustomerName(decodedDataRayaApps?.name);
      if (decodedDataRayaApps?.isRayaAgreement)
        setIsRayaAgreement(decodedDataRayaApps?.isRayaAgreement);
      if (decodedDataRayaApps?.status)
        setStatusAuthFlowRaya(decodedDataRayaApps?.status);
      if (decodedDataRayaApps?.code) {
        setCookie(CODE_RAYA_APPS, decodedDataRayaApps?.code, 10);
        setCodeAuthFlowRaya(decodedDataRayaApps?.code);
      }
    }

    /**
     * configure disabled enabled inspect element
     */
    if (process.env.REACT_APP_INSPECT_ELEMENT === 'true') {
      // Disable right-click
      document.addEventListener('contextmenu', e => e.preventDefault());

      // Disable shortcut for inspect
      document.onkeydown = e => {
        if (
          e.keyCode === 123 ||
          ctrlShiftKey(e, 'I') ||
          ctrlShiftKey(e, 'J') ||
          ctrlShiftKey(e, 'C') ||
          (e.ctrlKey && e.keyCode === 'U'.charCodeAt(0))
        )
          return false;
      };
    }
  }, [setParameterAccess, setDeviceInformation, setDataRayaApps]);

  React.useEffect(() => {
    function onlineHandler() {
      setIsOnline(true);
    }

    function offlineHandler() {
      setIsOnline(false);
    }

    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);

    return () => {
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
    };
  }, []);

  React.useEffect(() => {
    if (
      error === 'error_postalcode_district' ||
      error === 'error_postalcode_ktp' ||
      error === 'error_postalcode_company'
    ) {
      setShowPostalDrawer(true);
    }
  }, [error]);

  const handleBottomSheetExit = () => {
    setIsExitPopup(false);
    closeWebview(history);
  };

  const ctrlShiftKey = (e, keyCode) => {
    return (
      (e.ctrlKey || e.metaKey) &&
      e.shiftKey &&
      e.keyCode === keyCode.charCodeAt(0)
    );
  };

  const handleActionExitPopup = () => {
    setIsExitPopup(!isExitPopup);
    history.push(location.pathname);
  };

  const requestPermission = () => {
    if (window.Android) {
      window?.Android?.requestPhonePermissions();
    }

    history.push('/');
    location.reload();
    resetError();
  };

  const actionHandler = () => {
    if (isSecondApply !== 'Y') {
      setShowPostalDrawer(false);
      return;
    }

    const routeMap = {
      error_postalcode_district: '/person-info',
      error_postalcode_ktp: '/person-info',
      error_postalcode_company: '/occupation-info'
    };

    const targetRoute = routeMap[error];
    setShowPostalDrawer(false);

    if (targetRoute) {
      history.push(targetRoute);
    }
  };

  // Go to input new pin
  const resetPin = () => {
    if (error) {
      resetError();
    }
    setApplicationStatus('FRESH_START');
    history.push('/forgot-pin/input-new-pin');
  };

  const homeHandler = () => {
    resetError();
    history.push('/home');
  };

  const resetHandler = () => {
    resetError();
  };

  const contactCsHandler = () => {
    setError('contact-cs');
  };

  const loading = (
    <Backdrop className={classes.backdrop} open={appLoad}>
      <center>
        <CircularProgress />
      </center>
    </Backdrop>
  );

  const drawerMaintenance = (
    <DrawerMaintenance
      closeBottomSheet={() => {
        setIsMaintenance(false);
      }}
      descriptionBottomSheet={descriptionBottomSheet}
      openBottomSheet={isMaintenance}
      tittleBottomSheet={tittleBottomSheet}
    />
  );

  const drawerErrorCOCD = (
    <DrawerInfo
      actionButtonLabel="Hubungi Sapa Raya"
      onActionHandler={() => {
        setError('contact-cs');
      }}
      opened={error === 'error_cocd'}
      setOpened={() => {
        setError('contact-cs');
      }}
    >
      <WarningDrawerContent
        align="left"
        content="Data belum berhasil diverifikasi"
        srcImage="/images/assets/ktp_error.svg"
        subContent="Ada data yang tidak sesuai dan belum berhasil diverifikasi. Hubungi Sapa Raya untuk informasi lebih lanjut."
      />
    </DrawerInfo>
  );

  const labelMapPostalCode = {
    error_postalcode_ktp: 'KTP',
    error_postalcode_company: 'Alamat Bekerja'
  };
  const labelPostalCode = labelMapPostalCode[error] || '';
  const contentDrawerPostalCode = `Kode Pos ${labelPostalCode} Tidak Sesuai`;
  const subContentDrawerPostalCode = `Kode pos ${labelPostalCode} tidak sesuai dengan daerah yang dipilih. Mohon periksa kembali.`;

  const drawerErrorPostalCode = (
    <DrawerInfo
      actionButtonLabel="Tutup"
      onActionHandler={actionHandler}
      opened={showPostalDrawer}
      setOpened={actionHandler}
    >
      <WarningDrawerContent
        align="center"
        content={contentDrawerPostalCode}
        srcImage="/images/assets/ktp_error.svg"
        subContent={subContentDrawerPostalCode}
      />
    </DrawerInfo>
  );

  const drawerExit = (
    <DrawerInfo
      actionButtonLabel="Keluar aplikasi"
      buttonOutlined
      labelButtonOutlined="Batal"
      onActionHandler={handleBottomSheetExit}
      onActionOutlined={handleActionExitPopup}
      opened={isExitPopup}
      setOpened={handleActionExitPopup}
    >
      <WarningDrawerContent
        content="Apakah kamu ingin keluar aplikasi?"
        srcImage="/images/assets/update_data.svg"
      />
    </DrawerInfo>
  );

  const drawerPermissionLocation = (
    <DrawerInfo
      actionButtonLabel="Aktifkan Akses Lokasi"
      onActionHandler={requestPermission}
      opened={error === 'permission_location'}
      setOpened={requestPermission}
    >
      <WarningDrawerContent
        align="left"
        content="Aktifkan layanan lokasi di HP-mu"
        srcImage="/images/assets/permission_location.svg"
        subContent="Data lokasi digunakan untuk mengaktifkan fitur keamanan saat kamu masuk ke aplikasi dan bertransaksi di Pinang Flexi."
      />
    </DrawerInfo>
  );

  const drawerWeakPin = (
    <DrawerInfo
      actionButtonLabel="Ubah Pin"
      closeButton={false}
      onActionHandler={() => {
        const redirectPath =
          WEAK_PIN_REDIRECT_MAP[location.pathname] || '/create-pin';
        history.push(redirectPath);
        resetError();
      }}
      opened={error === 'weakpin'}
      setOpened={() => resetError()}
    >
      <WarningDrawerContent
        clickWarning={() => resetError()}
        content="Maaf, pin tidak berhasil diperbarui"
        srcImage="/images/assets/phone_number_rejected.svg"
        subContent="Pin kamu terlalu lemah, silahkan gunakan pin yang lebih kuat"
      />
    </DrawerInfo>
  );

  const drawerResetPin = (
    <DrawerInfo
      onActionOutlined={resetPin}
      opened={error === 'need_reset_pin'}
      setOpened={() => resetError()}
    >
      <WarningDrawerContent
        clickWarning={resetPin}
        content="Saatnya ganti PIN kamu"
        contentButton="Atur Ulang PIN Sekarang"
        srcImage="/images/assets/reset_pin.svg"
        subContent="Untuk keamanan transaksi Pinang Flexi tetap aman, perlu update PIN secara berkala."
      />
    </DrawerInfo>
  );

  const drawerLimitReceiveOTP = (
    <DrawerInfo
      onActionOutlined={() => resetError()}
      opened={error === 'otp_receive_limited'}
      setOpened={() => resetError()}
    >
      <WarningDrawerContent
        align={'left'}
        clickWarning={() => resetError()}
        content="Melebihi batas verifikasi OTP"
        contentButton="Oke"
        srcImage="/images/assets/limit_receive_otp.svg"
        subContent="Proses verifikasi kamu melebihi batas maksimal percobaan, kamu bisa coba lagi 1x24 jam, ya."
      />
    </DrawerInfo>
  );

  const drawerReject = (
    <CustomDrawer
      closeHandler={() => resetError()}
      drawer={
        error === 'user_reject' ||
        error === 'payroll_rej' ||
        error === 'cr_score_rej'
      }
      setDrawer={resetError}
    >
      <WarningDrawerContent
        clickWarning={() => resetError()}
        content="Kamu belum dapat melanjutkan proses pengajuan pinjaman"
        contentButton="Coba Lagi"
        srcImage="/images/assets/NIK sudah terdaftar.svg"
        subContent={` Kamu bisa coba lagi pada ${dueDateTimeFormat(
          new Date(nextEligibilityDate)
        )}`}
      />
    </CustomDrawer>
  );

  const errorDialogOverOld = (
    <DrawerInfo
      actionButtonLabel="Kembali ke Beranda"
      onActionHandler={homeHandler}
      opened={error === 'getting_older_young'}
      setOpened={homeHandler}
    >
      <WarningDrawerContent
        clickWarning={homeHandler}
        content="Maaf, melebihi batas usia"
        srcImage="/images/assets/batas usia.svg"
        subContent={`Batas usia 21-55 tahun untuk pengajuan pinjaman ${
          schemeCode === SCHEME_CODE_PNANG ? 'Pinang Flexi' : 'Dana Siaga'
        }.`}
      />
    </DrawerInfo>
  );

  const errorEnteredKtpNumber = (
    <DrawerInfo
      actionButtonLabel="Coba Lagi"
      buttonOutlined
      labelButtonOutlined="Hubungi Sapa Raya"
      onActionHandler={resetHandler}
      onActionOutlined={contactCsHandler}
      opened={error === 'entered_ktp_number_is_not_correct'}
      setOpened={resetHandler}
    >
      <WarningDrawerContent
        clickWarning={resetHandler}
        content="NIK tidak sesuai"
        srcImage="/images/assets/data_not_appropriate.svg"
        subContent="Pastikan NIK yang kamu masukkan sesuai dengan rekening yang terhubung dan coba lagi, ya. Hubungi Sapa Raya untuk bantuan lebih lanjut."
      />
    </DrawerInfo>
  );

  const resetDrawerNotEligibleFaceCompare = () => {
    resetError();
    location.pathname !== '/profile' && history.push('/profile');
  };

  const errorNotEligibleFaceCompare = (
    <DrawerInfo
      actionButtonLabel="Oke Mengerti"
      buttonOutlined
      labelButtonOutlined="Hubungi Sapa Raya"
      onActionHandler={resetDrawerNotEligibleFaceCompare}
      onActionOutlined={contactCsHandler}
      opened={error === 'not_eligible_face_compare'}
      setOpened={resetDrawerNotEligibleFaceCompare}
    >
      <WarningDrawerContent
        align="left"
        clickWarning={resetHandler}
        content="Kesempatan verifikasi habis, coba lagi besok ya"
        srcImage="/images/assets/not-eligible-face-compare.svg"
        subContent="Demi keamananmu, verifikasi dikunci sementara. Hubungi Sapa Raya untuk bantuan lebih lanjut."
      />
    </DrawerInfo>
  );

  const duplicateUser = (
    <DrawerInfo
      actionButtonLabel="Coba Lagi"
      buttonOutlined
      labelButtonOutlined="Hubungi Sapa Raya"
      onActionHandler={resetHandler}
      onActionOutlined={contactCsHandler}
      opened={error === 'nik_is_already_registered'}
      setOpened={resetHandler}
    >
      <WarningDrawerContent
        clickWarning={resetHandler}
        content="NIK terdaftar di nomor HP lain"
        srcImage="/images/assets/NIK sudah terdaftar.svg"
        subContent="Silakan masuk menggunakan nomor tersebut. Hubungi Sapa Raya untuk bantuan lebih lanjut."
      />
    </DrawerInfo>
  );

  const errorAccountBRI = (
    <DrawerInfo
      actionButtonLabel="Oke"
      onActionHandler={resetHandler}
      opened={error === 'account_bri_not_found'}
      setOpened={resetHandler}
    >
      <WarningDrawerContent
        content="Rekening BRI tidak ditemukan"
        srcImage="/images/assets/not-in-whitelist.svg"
        subContent="Terdapat kendala ketika verifikasi rekening kamu. Silakan coba lagi nanti, ya!"
      />
    </DrawerInfo>
  );

  const errorGeneral = (
    <DrawerInfo
      actionButtonLabel="Oke"
      buttonOutlined
      labelButtonOutlined="Hubungi Sapa Raya"
      onActionHandler={resetHandler}
      onActionOutlined={contactCsHandler}
      opened={error === 'general_error'}
      setOpened={resetHandler}
    >
      <WarningDrawerContent
        content="Yah, lagi ada gangguan.."
        srcImage="/images/assets/general_error.svg"
        subContent="Terdapat kendala pada layanan Pinang Flexi. Silakan coba lagi nanti, ya!"
      />
    </DrawerInfo>
  );

  const errorMinimumBalance = (
    <DrawerInfo
      onActionHandler={resetHandler}
      opened={error === 'error_minimum_balance'}
    >
      <WarningDrawerContent
        clickWarning={resetHandler}
        content="Yah, saldo rekening tidak mencukupi"
        contentButton="Coba Lagi"
        srcImage="/images/assets/minimum-balance.svg"
        subContent="Pastikan rekening kamu mempunyai saldo untuk bisa mengajukan pinjaman, ya."
      />
    </DrawerInfo>
  );

  const errorAccountIsNotOpen = (
    <DrawerInfo
      actionButtonLabel="Ke Beranda"
      onActionHandler={homeHandler}
      opened={error === 'account_is_not_open'}
      setOpened={homeHandler}
    >
      <WarningDrawerContent
        content="Rekening tidak aktif"
        srcImage="/images/assets/data_not_appropriate.svg"
        subContent="Pastikan rekening kamu aktif. Kamu bisa mengaktifkan rekening di kantor cabang terdekat."
      />
    </DrawerInfo>
  );

  const errorDialog = (
    <DrawerInfo
      onActionHandler={resetHandler}
      opened={error === 'incorrect_ktp'}
    >
      <WarningDrawerContent
        clickWarning={resetHandler}
        content="Data Tidak Sesuai"
        contentButton="Coba Lagi"
        srcImage="/images/assets/data_not_appropriate.svg"
        subContent="Maaf data yang kamu masukan tidak sesuai. Silahkan coba lagi"
      />
    </DrawerInfo>
  );

  const changeNumberSuccessHandler = () => {
    setShowSuccessChangeNumber(false);
    if (location.pathname === '/login') return;
    if (subCode === 'RAYA' && codeAuthFlowRaya === CODE_AUTH_FLOW_RAYA_0007) {
      phoneRegistration(
        phonePrefix(phoneNoRaya, PHONE_PREFIX_62TO08),
        history,
        true,
        () => setError('phone_not_registered')
      );
    } else if (
      subCode === 'RAYA' &&
      codeAuthFlowRaya === CODE_AUTH_FLOW_RAYA_0001
    ) {
      history.push('/login');
    } else {
      history.push('/');
    }
  };

  const drawerChangeNumber = (
    <DrawerInfo
      actionButtonLabel="Oke"
      onActionHandler={changeNumberSuccessHandler}
      opened={showSuccessChangeNumber}
      setOpened={changeNumberSuccessHandler}
    >
      <WarningDrawerContent
        align="left"
        clickWarning={() => resetError()}
        content="Nomor HP berhasil diubah"
        srcImage="/images/assets/change-sim-success.svg"
        subContent="Selamat, sekarang kamu sudah bisa menggunakan nomor yang baru ya!"
      />
    </DrawerInfo>
  );

  const errorOTPExpired = (
    <DrawerInfo
      actionButtonLabel="Coba Lagi"
      onActionHandler={() => resetError()}
      opened={error === 'otp-expired'}
      setOpened={() => resetError()}
    >
      <WarningDrawerContent
        clickWarning={() => resetError()}
        content="OTP sudah tidak aktif"
        srcImage="/images/assets/phone_number_rejected.svg"
        subContent="Periksa kembali otp terbaru di nomor hp kamu."
      />
    </DrawerInfo>
  );

  const errorSystemChangePhoneNumber = (
    <DrawerInfo
      actionButtonLabel="Oke, Mengerti"
      onActionHandler={homeHandler}
      opened={error === 'system_change_number'}
      setOpened={homeHandler}
    >
      <WarningDrawerContent
        align="left"
        clickWarning={homeHandler}
        content="Yah, lagi ada gangguan.."
        srcImage="/images/assets/gangguan_drawer.svg"
        subContent="Tenang, tim kami sedang perbaikin. Silakan coba lagi nanti, ya!"
      />
    </DrawerInfo>
  );

  const errorConnectionChangePhoneNumber = (
    <DrawerInfo
      actionButtonLabel="Oke, Mengerti"
      onActionHandler={homeHandler}
      opened={error === 'connection_change_number'}
      setOpened={homeHandler}
    >
      <WarningDrawerContent
        align="left"
        clickWarning={homeHandler}
        content="Yah, koneksinya terputus"
        srcImage="/images/assets/lost_connection_drawer.svg"
        subContent="Cek koneksi internet kamu dan ulangi proses verifikasi."
      />
    </DrawerInfo>
  );

  const drawerPhoneNumberChangeLimit = (
    <DrawerInfo
      actionButtonLabel="Oke"
      buttonOutlined
      labelButtonOutlined="Hubungi Sapa Raya"
      onActionHandler={resetHandler}
      onActionOutlined={() => {
        setError('contact-cs');
      }}
      opened={error === 'change_number_quota_exceeded'}
      setOpened={resetHandler}
    >
      <WarningDrawerContent
        align="left"
        content="Kuota Ubah Nomor HP Habis"
        srcImage="/images/assets/freeze-change-phone-number.svg"
        subContent={`Kamu bisa ganti nomor HP lagi pada ${formatDateIndonesia(
          userProfile?.freezeChangePhoneNumEnd
        )} Hubungi Sapa Raya untuk bantuan, ya!`}
      />
    </DrawerInfo>
  );

  return (
    <SafeAreaView>
      <div className={classes.content}>
        <ThemeProvider theme={theme}>
          {loading}
          <Suspense
            fallback={
              <span
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  margin: '-25px 0 0 -25px'
                }}
              />
            }
          >
            <Routes />
            {ERROR_EXCEPTION_HANDLING.includes(error) && (
              <Redirect to="/exception-handling" />
            )}
            <Snackbar open={!isOnline}>
              <MuiAlert
                className={classes.snackbarAlert}
                elevation={6}
                severity="error"
                variant="filled"
              >
                Tidak ada koneksi Internet
              </MuiAlert>
            </Snackbar>
          </Suspense>
          {drawerMaintenance}
          {drawerErrorCOCD}
          {drawerExit}
          {drawerPermissionLocation}
          {drawerWeakPin}
          {drawerResetPin}
          {drawerLimitReceiveOTP}
          {drawerErrorPostalCode}
          {drawerReject}
          {errorDialogOverOld}
          {errorEnteredKtpNumber}
          {duplicateUser}
          {errorAccountBRI}
          {errorGeneral}
          {errorMinimumBalance}
          {errorAccountIsNotOpen}
          {errorDialog}
          {errorOTPExpired}
          {drawerChangeNumber}
          {errorNotEligibleFaceCompare}
          {errorSystemChangePhoneNumber}
          {errorConnectionChangePhoneNumber}
          {drawerPhoneNumberChangeLimit}
        </ThemeProvider>
      </div>
    </SafeAreaView>
  );
};

const mapDispatchStatesToProps = states => {
  return {
    phoneNo: states.auth.phoneNo,
    appLoad: states.loanApply.loading,
    error: states.auth.error,
    applicationId: states.loanApply.applicationId,
    tittleBottomSheet: states.loanApply.bottomSheet?.tittleBottomSheet,
    descriptionBottomSheet:
      states.loanApply.bottomSheet?.descriptionBottomSheet,
    isMaintenance: states.loanApply.isMaintenance,
    applicationStatus: states.loanApply.applicationStatus,
    permissionGeolocation: states.auth.permissionGeolocation,
    isSecondApply: states.loanApply.appInqRes.isSecondApply,
    nextEligibilityDate: states.loanApply.nextDate,
    schemeCode:
      states?.loanApply?.loanScheme.schemeCode ||
      states.loanApply.appInqRes.schemeCode ||
      states.loanApply.schemeCode,
    subCode: states.auth.subCode,
    codeAuthFlowRaya: states.auth.codeAuthFlowRaya,
    phoneNoRaya: states.auth.noHpRaya,
    showSuccessChangeNumber: states.loanApply.showSuccessChangeNumber,
    userProfile: states.auth.userProfile
  };
};

App.propTypes = {
  codeAuthFlowRaya: PropTypes.string,
  isSecondApply: PropTypes.string,
  nextEligibilityDate: PropTypes.string,
  phoneNoRaya: PropTypes.string,
  phoneRegistration: PropTypes.func,
  schemeCode: PropTypes.string,
  setApplicationStatus: PropTypes.func,
  setShowSuccessChangeNumber: PropTypes.func,
  showSuccessChangeNumber: PropTypes.bool,
  subCode: PropTypes.string,
  userProfile: PropTypes.object
};

const mapDispatchToProps = dispatch => {
  return {
    appInquiry: auth => dispatch(actions.applicationInquiry(auth)),
    setApplicationStatus: status =>
      dispatch(actions.setApplicationStatus(status)),
    setParameterAccess: params => dispatch(actions.setParameterAccess(params)),
    setDeviceInformation: deviceInformation =>
      dispatch(actions.setDeviceInformation(deviceInformation)),
    setIsMaintenance: value => dispatch(actions.setIsMaintenance(value)),
    setDataRayaApps: data => dispatch(actions.setDataRayaApps(data)),
    setPhoneNumber: noHpFlexi => dispatch(actions.setPhoneNo(noHpFlexi)),
    setPhoneNumberRaya: noHpRaya => dispatch(actions.setPhoneNoRaya(noHpRaya)),
    setPhoneNumberFlexiNew: noHpFlexiNew =>
      dispatch(actions.setPhoneNumberFlexiNew(noHpFlexiNew)),
    setEktp: eKtp => dispatch(actions.setEktp(eKtp)),
    setEktpRaya: eKtpRaya => dispatch(actions.setEktpRaya(eKtpRaya)),
    setCustomerName: value => dispatch(actions.setCustomerName(value)),
    setStatusAuthFlowRaya: params =>
      dispatch(actions.setStatusAuthFlowRaya(params)),
    setCodeAuthFlowRaya: params =>
      dispatch(actions.setCodeAuthFlowRaya(params)),
    setIsRayaAgreement: params => dispatch(actions.setIsRayaAgreement(params)),
    phoneRegistration: (phoneNo, history, isLogin, action) =>
      dispatch(actions.phoneRegistration(phoneNo, history, isLogin, action)),
    setShowSuccessChangeNumber: val =>
      dispatch(actions.setShowSuccessChangeNumber(val)),
    setError: val => dispatch(actions.setError(val)),
    resetError: () => dispatch(actions.setError(''))
  };
};

export default connect(mapDispatchStatesToProps, mapDispatchToProps)(App);
